import { useCallback, useEffect, useState } from 'react';

/**
 * Global Cmd+K / Ctrl+K state for the command palette.
 * Mount once (in Layout) — the keydown listener is registered while the
 * consuming component is mounted and toggles the palette from anywhere.
 *
 * `openPalette` exists for the shell's search box (OO1): the design's top bar shows a
 * "Search properties, deals, contacts ⌘K" field, and clicking it must reach the same palette
 * the shortcut does - one door, two handles.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        // Take over the browser's default Cmd+K (search bar focus) app-wide.
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const close = useCallback(() => setOpen(false), []);
  const openPalette = useCallback(() => setOpen(true), []);

  return { open, close, openPalette };
}
